import React, {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {
  globalNamespace,
  isTeammateNamespace,
} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {AsyncLoader} from '../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {CardNoteModel} from '../../../models/api/cardNoteModel';
import {ContactDetailsSectionsEnum} from '../../../models/api/localPreferencesModel';
import {
  contactDetailsScrolledToSectionAction,
  fetchContactDetailsCardNotesAction,
} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {
  ContactDetailsPanelState,
  ContactDetailsScrollSectionsEnum,
} from '../../../store/panels/contacts/contactDetailsPanelState';
import {InlineEditingProps, useInlineEditing} from '../../common/inlineEditing/useInlineEditing';
import {useContactDetailsContext} from '../contactDetailsContext';
import {buildNoteTrackingPayload, ContactDetailsCardNoteActionsEnum} from '../contactDetailsTrackingHelpers';
import {ContactDetailsSectionLoadingSkeleton} from '../loadingSkeleton/contactDetailsSectionLoadingSkeleton';
import {ComposerNotePartial} from './contactNoteComposer';
import {ContactNoteComposerContainer} from './contactNoteComposerContainer';
import {ContactNoteContainer} from './contactNoteContainer';

/*
 * Props.
 */

interface ContactNotesProps {
  panel?: ContactDetailsPanelState | null;
  noteLoadingStatus?: AsyncStatusesEnum;
  card: CardModel | undefined;
  notes: ReadonlyArray<CardNoteModel>;
  userNamespace: string;
  createCardNoteAsync: (cardId: number, value: ComposerNotePartial) => Promise<void>;
  createNewCardAsync: () => Promise<CardModel | undefined>;
}

interface StyledContactNoteSpacerDivProps {
  $isReadOnly: boolean;
}

export type ContactNotesHandle = {
  onAddNote: () => void;
};

/*
 * Style.
 */

const StyledNotesContainerDiv = styled.div`
  display: flex;
  flex-flow: column;
  margin-top: 15px;
`;

const StyledContactNoteContainer = styled(ContactNoteContainer)`
  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;

const StyledContactNoteComposerContainer = styled(ContactNoteComposerContainer)`
  padding: 4px;
  border-radius: 16px;
  margin-bottom: 8px;
`;

const StyledContactNoteSpacerDiv = styled.div<StyledContactNoteSpacerDivProps>`
  margin-top: 4px;
  ${(p) =>
    p.$isReadOnly &&
    css`
      margin-top: 10px;
    `}
`;

const StyledErrorDiv = styled.div`
  padding: 32px 0;
`;

const StyledNoNotesDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  margin-bottom: 4px;
`;

/*
 * Component.
 */

export const ContactNotes = forwardRef<ContactNotesHandle, ContactNotesProps>((props, ref) => {
  const {card, notes, userNamespace, noteLoadingStatus, panel, createCardNoteAsync, createNewCardAsync} =
    props;
  const {isReadOnly, panelId} = useContactDetailsContext();
  const cardNamespace = card ? card.namespace : globalNamespace;
  const dispatch = useAppDispatch();
  const notesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!noteLoadingStatus) {
      dispatch(fetchContactDetailsCardNotesAction(panelId, card?.numberOfNotesViaNamespaces === 0));
    }
  }, [dispatch, panelId, noteLoadingStatus, card?.numberOfNotesViaNamespaces]);

  useEffect(() => {
    // Make sure we are loaded, have a target to scroll too and we want to scroll to notes.
    if (
      panel?.sectionToScrollTo !== ContactDetailsScrollSectionsEnum.NOTES ||
      panel?.notesStatus !== AsyncStatusesEnum.LOADED ||
      !notesRef.current
    ) {
      return;
    }
    notesRef.current.scrollIntoView({behavior: 'smooth', block: 'start'});
    dispatch(contactDetailsScrolledToSectionAction(panel.id));
  }, [dispatch, panel?.id, panel?.notesStatus, panel?.sectionToScrollTo]);

  const createCardNote = async (cardRef: CardModel, value: ComposerNotePartial) => {
    await createCardNoteAsync(cardRef.id, value);
    trackInterfaceEvent(
      InterfaceEventsEnum.P4_CONTACT_DETAILS_EDIT_NOTE,
      buildNoteTrackingPayload(ContactDetailsCardNoteActionsEnum.ADD, cardRef, value.namespace),
    );
  };

  const onSave = async (value: ComposerNotePartial) => {
    if (!card) {
      // Create the new card then add the note to the newly created card
      const newCard = await createNewCardAsync();
      if (!newCard) {
        return;
      }
      await createCardNote(newCard, value);
      return;
    }
    await createCardNote(card, value);
  };
  const newNoteInlineProps = useInlineEditing<ComposerNotePartial>({
    currentAttributeValue: {
      namespace: cardNamespace,
      text: '',
    },
    onSave,
  });

  useImperativeHandle(ref, () => ({
    onAddNote: () => {
      newNoteInlineProps.onEditStart();
    },
  }));

  if (
    (!noteLoadingStatus && card && card.numberOfNotesViaNamespaces > 0) ||
    noteLoadingStatus === AsyncStatusesEnum.LOADING
  ) {
    return (
      <ContactDetailsSectionLoadingSkeleton
        section={ContactDetailsSectionsEnum.NOTES}
        shouldRemoveSectionLoadingStyles
        shouldHideTitleSkeleton
      />
    );
  }
  if (noteLoadingStatus === AsyncStatusesEnum.FAILED) {
    return (
      <StyledErrorDiv>
        <AsyncLoader
          size={VisualSizesEnum.LARGE}
          status={AsyncStatusesEnum.FAILED}
          error={<Trans id="contact-details-error-loading-notes">Something went wrong.</Trans>}
          onRetryClick={() => {
            dispatch(fetchContactDetailsCardNotesAction(panelId));
          }}
        />
      </StyledErrorDiv>
    );
  }

  return (
    <>
      <StyledContactNoteSpacerDiv $isReadOnly={isReadOnly} ref={notesRef} />
      {!isReadOnly && renderAddNoteSection(cardNamespace, userNamespace, newNoteInlineProps)}
      {card && notes.length > 0 && (
        <StyledNotesContainerDiv>
          {notes.map((note) => (
            <StyledContactNoteContainer
              key={note.id}
              note={note}
              userNamespace={userNamespace}
              card={card}
              panelId={panelId}
            />
          ))}
        </StyledNotesContainerDiv>
      )}
      {notes.length === 0 && renderEmpty()}
    </>
  );
});

const renderAddNoteSection = (
  cardNamespace: string,
  userNamespace: string,
  newNoteInlineProps: InlineEditingProps<ComposerNotePartial>,
) => {
  if (!newNoteInlineProps.isEditing) {
    return null;
  }
  return (
    <StyledContactNoteComposerContainer
      value={newNoteInlineProps.editedValue}
      error={newNoteInlineProps.error}
      onEdit={newNoteInlineProps.onEdit}
      onEditSave={newNoteInlineProps.onEditSave}
      onEditCancel={newNoteInlineProps.onEditCancel}
      isSaving={newNoteInlineProps.isSaving}
      userNamespace={userNamespace}
      shouldOnlyShowPrivateNamespace={isTeammateNamespace(cardNamespace)}
    />
  );
};

function renderEmpty() {
  return (
    <StyledNoNotesDiv>
      <Trans id="contact-details-no-notes">No notes</Trans>
    </StyledNoNotesDiv>
  );
}
